import { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export const hl = {
  dim: '#6b7280',
  tag: '#9ca3af',
  str: '#86efac',
  kw: '#fbbf24',
} as const;

export type HlColor = keyof typeof hl;
export type Token = [HlColor, string];

interface Props {
  /** Raw code string — copied to clipboard. Derived from tokens when omitted. */
  code?: string;
  /** Optional language label displayed as a dim badge (e.g. "html", "bash") */
  language?: string;
  /** Syntax-highlighted tokens rendered as colored spans */
  tokens?: Token[];
  /** Custom rendering; takes precedence over tokens */
  children?: React.ReactNode;
}

export default function CodeBlock({ code, language, tokens, children }: Props) {
  const resolvedCode = code ?? tokens?.map(([, text]) => text).join('') ?? '';
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(resolvedCode);
    setCopied(true);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const content =
    children ??
    (tokens ? (
      tokens.map(([color, text], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} component="span" sx={{ color: hl[color] }}>
          {text}
        </Box>
      ))
    ) : (
      <Box component="span" sx={{ color: '#e5e7eb' }}>
        {resolvedCode}
      </Box>
    ));

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        bgcolor: '#0f1117',
        border: '1px solid #1e2433',
        borderRadius: '6px',
        pl: 2,
        pr: 0.5,
        py: 0.75,
        flex: 1,
        minWidth: 0,
        fontFamily: '"SF Mono", "Fira Code", "Fira Mono", monospace',
        fontSize: '0.6875rem',
        lineHeight: 1.5,
        gap: 1,
      }}>
      {language && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.6875rem',
            fontFamily: 'inherit',
            color: '#9ca3af',
            flexShrink: 0,
            userSelect: 'none',
          }}>
          {language}
        </Typography>
      )}
      <Box
        sx={{
          flex: 1,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          userSelect: 'all',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
        {content}
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
        <Button
          size="small"
          onClick={handleCopy}
          sx={{
            minWidth: 32,
            height: 28,
            px: 0.75,
            flexShrink: 0,
            color: copied ? '#4ade80' : '#9ca3af',
            borderRadius: '4px',
            '&:hover': { bgcolor: '#1e2433', color: '#e5e7eb' },
          }}>
          {copied ? (
            <CheckIcon sx={{ fontSize: 13 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          )}
        </Button>
      </Tooltip>
    </Stack>
  );
}
